// The room: who is here, what tables are open, and who is sitting at them.
//
// Discord decides the room for us -- everyone who launches the same Activity
// shares an `instance_id` -- so there is no invite code and no matchmaking
// queue. The people in the call are the people you can play.
//
// The connection is a long-lived `fetch` reading newline-delimited JSON rather
// than an EventSource, because only `fetch` can carry an Authorization header;
// the alternative is an access token in a URL, which the web server logs.

import type { Action, ObjectId } from '../rules/types.ts';
import type { DeckList } from '../rules/engine.ts';
import type { MatchFrame } from './remote.ts';

export interface LobbyMember {
  id: string;
  name: string;
  avatar: string | null;
  /** Gone quiet, but their seat is still held. */
  away?: boolean;
}

export interface LobbyTable {
  id: string;
  name: string;
  host: string;
  /** Two seats, by user id. */
  seats: (string | null)[];
  spectators: string[];
  status: 'open' | 'starting' | 'playing';
  ready: string[];
}

export interface LobbyView {
  members: LobbyMember[];
  tables: LobbyTable[];
}

/** A frame of a match, forwarded to whoever is playing or watching it. */
export type MatchListener = (frame: MatchFrame) => void;

export const EMPTY_LOBBY: LobbyView = { members: [], tables: [] };

/**
 * Talks to one room for as long as the Activity is open.
 *
 * Reconnects on its own: a dropped stream is the normal way a laptop lid
 * closing looks, and a lobby that silently stops updating is worse than one
 * that flickers.
 */
export class Lobby {
  private readonly instance: string;
  private readonly token: string;
  private readonly onView: (view: LobbyView) => void;
  /** Called with every match frame the server sends this client. */
  onMatch: MatchListener | null = null;
  /**
   * A second hook on room updates.
   *
   * The constructor callback belongs to whoever built the lobby; this is for
   * everything else that needs to know -- the HUD naming an opponent, say --
   * without them having to wrap each other.
   */
  onViewChange: (() => void) | null = null;
  private closed = false;
  private beat: ReturnType<typeof setInterval> | null = null;

  view: LobbyView = EMPTY_LOBBY;

  constructor(instance: string, token: string, onView: (view: LobbyView) => void) {
    this.instance = instance;
    this.token = token;
    this.onView = onView;
  }

  private url(path: string): string {
    return `/api/room/${encodeURIComponent(this.instance)}${path}`;
  }

  private headers(json = false): HeadersInit {
    return {
      Authorization: `Bearer ${this.token}`,
      ...(json ? { 'Content-Type': 'application/json' } : {}),
    };
  }

  /** Opens the stream and keeps it open. Returns once the first view arrives. */
  async connect(): Promise<void> {
    void this.pump();
    // Presence is a heartbeat rather than the stream itself: the stream can
    // stay open through a sleep that the server should still treat as away.
    this.beat = setInterval(() => {
      void fetch(this.url('/heartbeat'), { method: 'POST', headers: this.headers() })
        .catch(() => {});
    }, 20_000);

    // Give the first frame a moment so the lobby does not render empty and
    // then jump; it is not worth blocking on if the server is slow.
    await new Promise<void>((resolve) => {
      const started = Date.now();
      const check = setInterval(() => {
        if (this.view !== EMPTY_LOBBY || Date.now() - started > 2500) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    });
  }

  private async pump(): Promise<void> {
    while (!this.closed) {
      try {
        const res = await fetch(this.url('/stream'), { headers: this.headers() });
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // Newline-delimited: everything before the last newline is complete.
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const msg = JSON.parse(line) as { t: string } & Record<string, unknown>;
              if (msg.t === 'lobby') {
                this.view = {
                  members: msg.members as LobbyMember[],
                  tables: msg.tables as LobbyTable[],
                };
                this.onView(this.view);
                this.onViewChange?.();
              } else if (msg.t === 'match') {
                this.onMatch?.(msg as unknown as MatchFrame);
              }
            } catch { /* a partial frame; the next read completes it */ }
          }
        }
      } catch (err) {
        if (!this.closed) console.warn('lobby stream dropped:', err);
      }
      if (this.closed) return;
      await new Promise((r) => setTimeout(r, 1500));
    }
  }

  private post<T>(path: string, body: unknown = {}): Promise<T | null> {
    return fetch(this.url(path), {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify(body),
    })
      .then((r) => (r.ok ? r.json() as Promise<T> : null))
      .catch(() => null);
  }

  open(name: string): Promise<{ id: string } | null> {
    return this.post<{ id: string }>('/open', { name });
  }

  sit(table: string): Promise<unknown> { return this.post('/sit', { table }); }
  spectate(table: string): Promise<unknown> { return this.post('/spectate', { table }); }
  ready(table: string, ready: boolean, deck: DeckList | null = null): Promise<unknown> {
    return this.post('/ready', { table, ready, deck });
  }

  /** Submits an action in a match. The server decides whether it happens. */
  async act(table: string, action: Action): Promise<boolean> {
    const got = await this.post<{ ok?: boolean }>('/action', { table, action });
    return got?.ok === true;
  }
  /**
   * Says where a card in your base was put, so the other side sees it too.
   *
   * Not an action: nothing in the rules moved. It is sent all the same,
   * because a board only one player can read is not a shared board.
   */
  async arrange(table: string, object: ObjectId, cell: [number, number]): Promise<void> {
    await this.post('/arrange', { table, object, cell });
  }

  leave(): Promise<unknown> { return this.post('/leave'); }

  close(): void {
    this.closed = true;
    if (this.beat) clearInterval(this.beat);
    this.beat = null;
  }
}
